import { MessageBarType } from '@fluentui/react';
import React, { createContext, useCallback, useMemo, useReducer } from 'react';
import { newId } from '../helpers';

const NotificationsContext = createContext();
const NotificationsManagerContext = createContext();

export const NotificationsAction = {
    alert: "alert",
    clear: "clear",
    confirm: "confirm",
    dismiss: "dismiss",
    inform: "inform",
};

export const NotificationContextProvider = ({ children }) => {

    const addNotification = useCallback((notifications, id, type, message, code) => {
        id = id ?? newId();
        return [...notifications, { code, id, message, type }];
    }, []);

    const clear = useCallback((notifications) => {
        if (notifications.some(notification => notification.type !== MessageBarType.info))
            return notifications.filter(notification => notification.type === MessageBarType.info);
        else return notifications;
    }, []);

    const dismissNotification = useCallback((notifications, id) => {
        return notifications.filter(notification => notification.id !== id);
    }, []);

    const reducer = useCallback((notifications, action) => {
        if (!!action) {
            const { code, id, message, type } = action;
            switch (type) {
                case NotificationsAction.alert: return addNotification(notifications, id, MessageBarType.error, message, code);
                case NotificationsAction.clear: return clear(notifications);
                case NotificationsAction.confirm: return addNotification(notifications, id, MessageBarType.success, message);
                case NotificationsAction.dismiss: return dismissNotification(notifications, id);
                case NotificationsAction.inform: return addNotification(notifications, id, MessageBarType.info, message);
                default: throw new Error(`Action non reconnue : ${action.type}`)
            }
        }
    }, [addNotification, clear, dismissNotification]);

    // Exemple : [
    //    { id: 1, message: "Chargement...", type: MessageBarType.info },
    //    { id: 2, message: "Une erreur est survenue...", type: MessageBarType.error, code: 100 }
    // ]
    const [notifications, dispatch] = useReducer(reducer, []);

    const errorMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.error),
        [notifications]
    );

    const infoMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.info),
        [notifications]
    );

    const successMessages = useMemo(
        () => notifications.filter(notification => notification.type === MessageBarType.success),
        [notifications]
    );

    const alert = useCallback((message, code) => {
        if (!message) message = "Une erreur est survenue";
        dispatch({ type: NotificationsAction.alert, message, code });
    }, []);

    const confirm = useCallback((message) => {
        dispatch({ type: NotificationsAction.confirm, message });
    }, []);

    const inform = useCallback((message) => {
        const id = newId();
        dispatch({ type: NotificationsAction.inform, id, message });
        return id;
    }, []);

    const dismiss = useCallback((id) => {
        dispatch({ type: NotificationsAction.dismiss, id });
    }, []);

    const extractErrorMessages = useCallback((response) => {
        const { codeRetour, messageInfo, messageRetour } = response;
        if (!!messageInfo) alert(messageInfo, codeRetour);
        if (!!messageRetour) alert(messageRetour, codeRetour);
    }, [alert]);

    const value = useMemo(
        () => ({ notifications, errorMessages, infoMessages, successMessages }),
        [notifications, errorMessages, infoMessages, successMessages]
    );

    const managerValue = useMemo(
        () => ({ confirm, extractErrorMessages, inform, dismiss }),
        [confirm, extractErrorMessages, inform, dismiss]
    );

    return <NotificationsManagerContext.Provider value={managerValue}>
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    </NotificationsManagerContext.Provider>;

};

export const useNotifications = () => {
    const data = React.useContext(NotificationsContext);
    if (data === undefined) throw new Error();
    const { notifications, errorMessages, infoMessages, successMessages } = data;
    return { notifications, errorMessages, infoMessages, successMessages };
};

export const useNotificationsManager = () => {
    const manager = React.useContext(NotificationsManagerContext);
    if (manager === undefined) throw new Error();
    const { confirm, extractErrorMessages, inform, dismiss } = manager;
    return { confirm, extractErrorMessages, inform, dismiss };
};